import { call, select } from 'redux-saga/effects'
import { DashboardSchemaModelShape } from './../models/DashboardSchemaModel.d'

import { StorageKeys, storeKeyedValue } from '@aytm/app/insights/dashboard/lib/storage'

import {
  InsightRequestChain,
  InsightsDashboardPoolState,
  InsightsQueueItem,
  type InsightsDashboardErrorTypes
} from '@aytm/app/insights/dashboard/duckness/ducks/types'
import {
  getSplitOptions,
  getWaveOptions,
  getWaveUidsSet
} from '@aytm/app/insights/dashboard/helpers/itemsTransformation'
import {
  BlockModel,
  FiltersOrderedMap,
  getEmptyBlockModel,
  getEmptyFiltersOrderedMap,
  type BlockModelShape
} from '@aytm/app/insights/dashboard/models/BlockModel'
import { DashboardSchemaModel } from '@aytm/app/insights/dashboard/models/DashboardSchemaModel'
import { ComponentStoreState, InsightsErrors } from '@aytm/app/insights/dashboard/stores/ComponentStoreState'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import { areObjectsEqual } from '@aytm/app/insights/dashboard/lib/comparison'
import { isIdentityVector } from '@aytm/app/insights/dashboard/lib/arrays'

export const clearError = (errors: InsightsErrors, types?: InsightsDashboardErrorTypes[]): InsightsErrors =>
  types?.length
    ? types.reduce(
        (acc, type) => {
          delete acc[type]
          return acc
        },
        { ...(errors || {}) } as InsightsErrors
      )
    : errors

export const dismissError = (errors: InsightsErrors, type: InsightsDashboardErrorTypes) => {
  const existingErrors = errors?.[type] || []
  if (0 === existingErrors.length) {
    return errors
  }
  const [, ...remainingErrors] = existingErrors
  return remainingErrors.length
    ? ({ ...errors, [type]: remainingErrors } as InsightsErrors)
    : clearError(errors, [type])
}

export const addError = (
  errors: InsightsErrors,
  type: InsightsDashboardErrorTypes,
  error: Error | string | string[]
) => {
  const incomingErrors = Array.isArray(error) ? error : [error instanceof Error ? error.message : error]
  const existingErrors = errors?.[type] || []
  // eslint-disable-next-line immutablejs/no-native-map-set
  const newErrors = [...new window.Set([...existingErrors, ...incomingErrors])]
  return { ...errors, [type]: newErrors }
}

export const updateCurrentSchema = (
  state: InsightsDashboardPoolState,
  updates: Partial<DashboardSchemaModelShape> = {}
): InsightsDashboardPoolState => {
  const currentSchema = state.currentSchema?.merge(updates)
  return { ...state, currentSchema }
}

export const updateRemoteSchema = (
  state: InsightsDashboardPoolState,
  updates: Partial<DashboardSchemaModelShape> = {}
): InsightsDashboardPoolState => {
  const remoteSchema = state.remoteSchema?.merge(updates)
  return { ...state, remoteSchema }
}

export const updateBlock = (
  state: InsightsDashboardPoolState,
  blockUID: string,
  updates: Partial<BlockModelShape> = {}
): InsightsDashboardPoolState => {
  const currentSchema = state.currentSchema?.updateIn<BlockModel>(['blocks', blockUID], block =>
    (block || getEmptyBlockModel()).merge(updates)
  )
  return { ...state, currentSchema }
}

type UpdaterType = (block: BlockModel) => Partial<BlockModelShape>

export const updateCurrentBlock = (
  state: InsightsDashboardPoolState,
  updates: Partial<BlockModelShape> | UpdaterType
): InsightsDashboardPoolState => {
  const currentBlockUID = state.currentSchema?.get<string>('currentBlockUID') || null

  const effectiveUpdates =
    'function' === typeof updates
      ? updates(state.currentSchema?.getIn<BlockModel>(['blocks', currentBlockUID]) || getEmptyBlockModel())
      : updates

  if ('sortOrderPositions' in effectiveUpdates && isIdentityVector(effectiveUpdates['sortOrderPositions'])) {
    // debugger
    effectiveUpdates['sortOrderPositions'] = null
  }
  return updateBlock(state, currentBlockUID, effectiveUpdates)
}

export const updateCurrentData = (
  state: InsightsDashboardPoolState,
  updates: Partial<ComponentStoreState>
): InsightsDashboardPoolState => {
  const currentData = Object.assign(state.currentData || {}, updates) as ComponentStoreState
  return { ...state, currentData }
}

export const updateState = (
  state: InsightsDashboardPoolState,
  updates: Partial<InsightsDashboardPoolState>
): InsightsDashboardPoolState => {
  return { ...state, ...updates }
}

export const mergeSources = (src: DashboardSchemaModel, dest: DashboardSchemaModel): DashboardSchemaModel =>
  src && dest
    ? src.merge({
        createdAt: dest.createdAt,
        createdBy: dest.createdBy,
        updatedAt: dest.updatedAt,
        updatedBy: dest.updatedBy,
        filters: dest.filters
      })
    : src

export const updatePortalModified = (state: InsightsDashboardPoolState): InsightsDashboardPoolState => {
  const current = state.currentSchema?.get<FiltersOrderedMap>('filters') || getEmptyFiltersOrderedMap()
  const remote = state.remoteSchema?.get<FiltersOrderedMap>('filters')
  const portalModified =
    current &&
    remote &&
    !areObjectsEqual((current?.toJS?.() || current) as object, (remote?.toJS?.() || remote) as object, {})
  return { ...state, portalModified }
}

export const updateRequestChain = (
  state: InsightsDashboardPoolState,
  params: Partial<Pick<InsightRequestChain, 'currentIndex' | 'progress'>> = {}
): InsightsDashboardPoolState => {
  const requestChain = state.requestChain
  return {
    ...state,
    requestChain: {
      ...requestChain,
      ...params
    }
  }
}

export const updateRequestQueue = (
  state: InsightsDashboardPoolState,
  params: Partial<InsightsQueueItem> = {}
): InsightsDashboardPoolState => {
  const requestChain = state.requestChain
  const currentIndex = requestChain.currentIndex
  // If currentIndex is not set, use params.key to find the index
  // If params.key is not set, use currentIndex to find the index
  if (currentIndex >= 0 || params.key) {
    const queue = state.requestChain.queue.slice()
    // Find the index of the item in the queue
    let queueIndex = params.key ? queue.findIndex(v => v.key === params.key) : currentIndex
    // Get previous status
    const oldStatus = queueIndex >= 0 ? queue[queueIndex].status : null
    // Update queue
    if (queueIndex >= 0) {
      // Update existing item in the queue
      queue[queueIndex] = { ...queue[queueIndex], ...params }
    } else {
      // Add new item to the queue
      queue.push(params as InsightsQueueItem)
      queueIndex = queue.length - 1
    }
    // Calculate new index if status changed to 'progress'
    const newIndex = 'progress' === params.status && oldStatus !== params.status ? queueIndex : currentIndex
    // Calculate progress based on status changes
    const progress =
      'progress' === params.status && oldStatus !== params.status
        ? 0
        : 'completed' === params.status && oldStatus !== params.status
        ? 100
        : requestChain.progress
    // Return updated state
    return {
      ...state,
      requestChain: {
        ...state.requestChain,
        currentIndex: newIndex,
        progress,
        queue
      }
    }
  }
  // Return unchanged state if currentIndex is not set and params.key is not provided
  return state
}

export const updateLoading = (
  state: InsightsDashboardPoolState,
  start: boolean,
  timestamp?: number
): InsightsDashboardPoolState => {
  let resultState = state
  if (start) {
    if (!state.isLoading) {
      resultState = updateState(state, {
        isLoading: true,
        isLoadingTimestamp: timestamp || Date.now()
      })
    }
    if (state.exportState?.isExporting) {
      resultState = updateState(state, {
        exportState: { ...state.exportState, isExporting: false }
      })
    }
  } else {
    if (state.isLoading) {
      resultState = updateState(state, {
        isLoading: false,
        isFreshLoading: false,
        isLoadingTimestamp: null
      })
    }
  }
  return resultState
}

export const getDataItemsProgress = (state: InsightsDashboardPoolState): number => {
  const data = state.currentData?.data || {}
  const totalItems = Object.keys(data).length
  const fetchedItems = Object.values(data).filter(item => 'object' === typeof item).length
  const progress = totalItems > 0 ? Math.round((fetchedItems / totalItems) * 100) : 100
  return progress
}

export const getStatItemsProgress = (state: InsightsDashboardPoolState): number => {
  const stat = state.currentData?.stat || {}
  const totalItems = Object.keys(stat).length
  const fetchedItems = Object.values(stat).filter(item => 'object' === typeof item).length
  const progress = totalItems > 0 ? Math.round((fetchedItems / totalItems) * 100) : 100
  return progress
}

export const getBlock = (state: InsightsDashboardPoolState, uid: string): BlockModel | null =>
  state.currentSchema?.getIn<BlockModel>(['blocks', uid]) || null

export const getCurrentBlockUID = (state: InsightsDashboardPoolState): string | null =>
  state.currentSchema?.get<string>('currentBlockUID') || null

export const getCurrentBlock = (state: InsightsDashboardPoolState): BlockModel | null => {
  const currentBlockUID = getCurrentBlockUID(state)
  return getBlock(state, currentBlockUID)
}

export const updateSplitOptions = (state: InsightsDashboardPoolState): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray() || []
  const currentSplit = block?.get<string>('currentSplit') || null
  const subjects = state.currentData?.subjects || {}
  const waveUids = getWaveUidsSet(items)
  const splitOptions = currentSplit && subjects ? getSplitOptions(subjects, waveUids) : []
  const waveOptions = currentSplit && subjects ? getWaveOptions(subjects, waveUids) : []
  return updateCurrentData(state, { splitOptions, waveOptions })
}

export function* saveCurrentSchema(_action?: { type: string }): Generator<unknown, void, DashboardSchemaModel> {
  const storageSchema: DashboardSchemaModel = yield select((state: InsightsDashboardPoolState) => state.currentSchema)
  yield call(
    storeKeyedValue,
    staticStore.isSharedView ? StorageKeys.DASHBOARD_SCHEMA_SHARED : StorageKeys.DASHBOARD_SCHEMA,
    staticStore.dashboardID + (staticStore.isSharedView ? '_' + staticStore.currentSharedKey : ''),
    storageSchema
  )
}

export const getSubjectType = (state: InsightsDashboardPoolState) => {
  const block = getCurrentBlock(state)
  return block?.getSubjectType() || null
}
